import { Lock } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { formatCurrency } from '@/lib/utils'
// One definition, in @/types. The copy that used to live here had already
// drifted — its `description` was optional where the canonical one is
// required — which is how a field gaining a property on the Rails side ends
// up updated in one copy and silently stale in the other.
import type { FieldType, FieldDefinition } from '@/types'

type FieldValueData = {
  field_id: string
  value: string | number | null
  option_ids: string[]
  option_labels?: string[]
}

interface FieldValuesProps {
  fields: FieldDefinition[]
  values: FieldValueData[]
}

export function FieldValues({ fields, values }: FieldValuesProps) {
  const sortedFields = [...fields].sort((a, b) => a.position - b.position)

  const getValueForField = (fieldId: string): FieldValueData | undefined => {
    return values.find(v => v.field_id === fieldId)
  }

  const getOptionLabels = (field: FieldDefinition, optionIds: string[]): string[] => {
    return optionIds
      .map(id => field.options.find(o => o.id === id)?.label)
      .filter(Boolean) as string[]
  }

  if (sortedFields.length === 0) {
    return null
  }

  return (
    <div className="space-y-4">
      {sortedFields.map(field => {
        const fieldValue = getValueForField(field.id)
        if (!fieldValue && !field.required) return null

        return (
          <div key={field.id} className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-muted-foreground">
                {field.label}
              </span>
              {field.encrypted && <Lock className="h-3 w-3 text-muted-foreground" />}
            </div>
            <div className="text-sm">
              {renderValue(field, fieldValue, getOptionLabels)}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function renderValue(
  field: FieldDefinition,
  fieldValue: FieldValueData | undefined,
  getOptionLabels: (f: FieldDefinition, ids: string[]) => string[]
) {
  if (!fieldValue) {
    return <span className="text-muted-foreground">Not provided</span>
  }

  const { value, option_ids, option_labels } = fieldValue

  switch (field.field_type) {
    case 'text':
    case 'email':
    case 'phone':
      return <span>{value || <span className="text-muted-foreground">Not provided</span>}</span>

    case 'textarea':
      return (
        <div className="whitespace-pre-wrap bg-muted/50 rounded-md p-3 text-sm">
          {value || <span className="text-muted-foreground">Not provided</span>}
        </div>
      )

    case 'number':
      return <span>{value !== null ? value : <span className="text-muted-foreground">Not provided</span>}</span>

    case 'date':
      return (
        <span>
          {value
            ? new Date(value as string).toLocaleDateString()
            : <span className="text-muted-foreground">Not provided</span>
          }
        </span>
      )

    case 'currency':
      const numValue = value as number
      return (
        <span>
          {numValue !== null && numValue !== undefined
            ? formatCurrency(numValue)
            : <span className="text-muted-foreground">Not provided</span>
          }
        </span>
      )

    case 'select':
      const labels = option_labels || getOptionLabels(field, option_ids)
      return <span>{labels[0] || <span className="text-muted-foreground">Not selected</span>}</span>

    case 'multi_select':
      const multiLabels = option_labels || getOptionLabels(field, option_ids)
      if (multiLabels.length === 0) {
        return <span className="text-muted-foreground">None selected</span>
      }
      return (
        <div className="flex flex-wrap gap-1">
          {multiLabels.map((label, i) => (
            <Badge key={i} variant="secondary" className="text-xs">
              {label}
            </Badge>
          ))}
        </div>
      )

    case 'attachment':
      const files = value as unknown as string[]
      if (!files || files.length === 0) {
        return <span className="text-muted-foreground">No files attached</span>
      }
      return (
        <div className="space-y-1">
          {files.map((file, i) => (
            <div key={i} className="text-sm text-primary">
              {file}
            </div>
          ))}
        </div>
      )

    default:
      return <span>{value || <span className="text-muted-foreground">Not provided</span>}</span>
  }
}

export type { FieldDefinition, FieldValueData, FieldType }
